import React from 'react';
import { useAppDispatch } from '../app/hooks';
import { createCheckoutSession } from '../app/slices/subscriptionSlice';
import { XMarkIcon, CheckIcon, SparklesIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { trackError, ERROR_CODES, createErrorMeta, trackUsage } from '../lib/telemetry';
import { isFeatureEnabled } from '../lib/flags';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  isTestStripePk,
  PAYWALL_MODE,
  STRIPE_PUBLISHABLE_KEY,
  STRIPE_BUY_BTN_MONTHLY,
  STRIPE_BUY_BTN_ANNUAL,
  STRIPE_LINK_MONTHLY,
  STRIPE_LINK_ANNUAL,
} from '../lib/env';
import EmbeddedCheckout from './EmbeddedCheckout';

// Note: stripe-buy-button is a custom element loaded from Stripe's CDN
// TypeScript doesn't know about it, so we use type assertion

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?:
    | 'FREE'
    | 'PRO-MONTHLY'
    | 'PRO-ANNUAL'
    | 'PRO_TRIAL'
    | 'ARCHIVE'
    | 'ARCHIVE-ONE-TIME';
  preselectedPlan?: 'monthly' | 'annual';
}

interface PricingInfo {
  monthly: {
    amount: number;
    currency: string;
    displayAmount: string;
  };
  annual: {
    amount: number;
    currency: string;
    displayAmount: string;
  };
  savings: {
    amount: number;
    displayAmount: string;
    percentage: number;
    currency: string;
  };
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  currentPlan = 'FREE',
  preselectedPlan,
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState<'month' | 'year' | null>(null);
  const [useBuyButtons, setUseBuyButtons] = React.useState(false);
  const [usePaymentLinks, setUsePaymentLinks] = React.useState(false);
  const [useEmbeddedCheckout, setUseEmbeddedCheckout] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [stripeScriptLoaded, setStripeScriptLoaded] = React.useState(false);
  const [pricingInfo, setPricingInfo] = React.useState<PricingInfo | null>(null);
  const [pricingLoading, setPricingLoading] = React.useState(true);

  // Fetch dynamic pricing from Stripe
  React.useEffect(() => {
    if (!isOpen) return;

    const fetchPricing = async () => {
      try {
        setPricingLoading(true);
        const getPricingInfoFn = httpsCallable<{}, PricingInfo>(functions, 'getPricingInfo');
        const result = await getPricingInfoFn();
        setPricingInfo(result.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Fallback to hardcoded pricing if fetch fails
        setPricingInfo({
          monthly: { amount: 1200, currency: 'cad', displayAmount: '12.00' },
          annual: { amount: 12000, currency: 'cad', displayAmount: '120.00' },
          savings: { amount: 2400, displayAmount: '24.00', percentage: 17, currency: 'cad' },
        });
      } finally {
        setPricingLoading(false);
      }
    };

    fetchPricing();
  }, [isOpen]);

  // Check for query override and feature flag + env mode
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryMode = urlParams.get('mode'); // 'buy' | 'links' | 'embedded' | null
    const flagEnabled = isFeatureEnabled('USE_STRIPE_BUY_BUTTONS');
    const envSaysBuy = PAYWALL_MODE === 'BUY_BUTTONS';
    const envSaysLinks = PAYWALL_MODE === 'PAYMENT_LINKS';
    const envSaysEmbedded = PAYWALL_MODE === 'EMBEDDED_CHECKOUT';

    const hasAllBuyButtons = !!(STRIPE_BUY_BTN_MONTHLY && STRIPE_BUY_BTN_ANNUAL);
    const hasAnyBuyButtons = !!(STRIPE_BUY_BTN_MONTHLY || STRIPE_BUY_BTN_ANNUAL);
    const hasPaymentLinks = !!(STRIPE_LINK_MONTHLY && STRIPE_LINK_ANNUAL);
    const hasStripeKey = !!STRIPE_PUBLISHABLE_KEY;

    // Query overrides take precedence
    let shouldUseBuyButtons = queryMode === 'buy' || (!queryMode && (flagEnabled || envSaysBuy));
    let shouldUsePaymentLinks = queryMode === 'links' || (!queryMode && envSaysLinks);
    let shouldUseEmbedded =
      queryMode === 'embedded' || (!queryMode && (envSaysEmbedded || hasStripeKey));

    // Priority: Embedded > Buy Buttons > Payment Links > Checkout Sessions
    if (shouldUseEmbedded && hasStripeKey) {
      shouldUseBuyButtons = false;
      shouldUsePaymentLinks = false;
    } else if (shouldUseBuyButtons && !hasAllBuyButtons && hasPaymentLinks) {
      // If Buy Buttons requested but not fully configured, fall back to links if available
      shouldUseBuyButtons = false;
      shouldUsePaymentLinks = true;
      shouldUseEmbedded = false;
    } else if (!queryMode && !hasAnyBuyButtons && !hasPaymentLinks && !hasStripeKey) {
      // If nothing configured, use Checkout Sessions default path
      shouldUseBuyButtons = false;
      shouldUsePaymentLinks = false;
      shouldUseEmbedded = false;
    }

    setUseBuyButtons(shouldUseBuyButtons);
    setUsePaymentLinks(shouldUsePaymentLinks);
    setUseEmbeddedCheckout(shouldUseEmbedded);

    // Load Stripe script once if using Buy Buttons
    if (shouldUseBuyButtons && !stripeScriptLoaded) {
      const id = 'stripe-buy-button-js';
      if (!document.getElementById(id)) {
        const script = document.createElement('script');
        script.id = id;
        script.src = 'https://js.stripe.com/v3/buy-button.js';
        script.async = true;
        script.onload = () => setStripeScriptLoaded(true);
        script.onerror = () => {
          // eslint-disable-next-line no-console
          console.warn('Stripe buy-button element failed to load; check CSP and network.');
        };
        document.head.appendChild(script);
      } else {
        setStripeScriptLoaded(true);
      }
    }

    // Configuration info for debugging
    // eslint-disable-next-line no-console
    console.info('PAYWALL_CONFIG', {
      envMode: PAYWALL_MODE,
      effectiveMode: shouldUseEmbedded
        ? 'EMBEDDED_CHECKOUT'
        : shouldUseBuyButtons
          ? 'BUY_BUTTONS'
          : shouldUsePaymentLinks
            ? 'PAYMENT_LINKS'
            : 'CHECKOUT_SESSIONS',
      stripePublishableKeyConfigured: !!STRIPE_PUBLISHABLE_KEY,
      buyButtons: {
        monthly: !!STRIPE_BUY_BTN_MONTHLY,
        annual: !!STRIPE_BUY_BTN_ANNUAL,
      },
      paymentLinks: {
        monthly: !!STRIPE_LINK_MONTHLY,
        annual: !!STRIPE_LINK_ANNUAL,
      },
      currentPlan,
      preselectedPlan,
    });
  }, [stripeScriptLoaded, currentPlan, preselectedPlan]);

  // Track buy button render
  React.useEffect(() => {
    if (isOpen && useBuyButtons && stripeScriptLoaded) {
      trackUsage('usage_buy_button_render').catch(console.warn);
    }
  }, [isOpen, useBuyButtons, stripeScriptLoaded]);

  // Warn if buy-button element isn't registered (CSP or script blocked)
  React.useEffect(() => {
    if (!useBuyButtons) return;
    const isRegistered =
      typeof window !== 'undefined' &&
      (window as any).customElements &&
      typeof (window as any).customElements.get === 'function' &&
      !!(window as any).customElements.get('stripe-buy-button');
    if (!isRegistered) {
      // eslint-disable-next-line no-console
      console.warn('Stripe buy-button element not registered; check CSP and script load.');
    }
  }, [useBuyButtons, stripeScriptLoaded]);

  // Stripe publishable key (from env)
  const stripePublishableKey = STRIPE_PUBLISHABLE_KEY || '';

  const handleSubscribe = async (interval: 'month' | 'year') => {
    setIsLoading(interval);
    try {
      const result = await dispatch(createCheckoutSession({ interval })).unwrap();
      const promoInfo = result.promoAutoApplied ? { promo: 'auto' } : {};
      trackUsage('usage_checkout_click', {
        plan: interval === 'month' ? 'monthly' : 'annual',
        ...promoInfo,
      }).catch(console.warn);

      // If using embedded checkout, set clientSecret to show embedded form
      if (useEmbeddedCheckout && result.clientSecret) {
        console.log('[PaywallModal] Using Embedded Checkout with client_secret');
        setClientSecret(result.clientSecret);
      } else {
        // Otherwise, redirect to hosted checkout
        console.log('[PaywallModal] Redirecting to hosted Stripe Checkout');
        window.location.href = result.url;
      }
    } catch (error: any) {
      const errorMeta = createErrorMeta(
        'payments',
        ERROR_CODES.PAYMENT_CHECKOUT_CREATE_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          userAction: 'checkout',
          extras: { interval },
        }
      );
      trackError('Failed to start checkout process', errorMeta, error);
      // Error is handled by the slice and shown in UI
    } finally {
      setIsLoading(null);
    }
  };

  const handleBuyButtonClick = (plan: 'pro_monthly' | 'pro_annual') => {
    // eslint-disable-next-line no-console
    console.info('PAYWALL_CLICK', {
      mode: PAYWALL_MODE,
      monthlyOk: !!STRIPE_LINK_MONTHLY,
      annualOk: !!STRIPE_LINK_ANNUAL,
      pkTest: isTestStripePk(STRIPE_PUBLISHABLE_KEY),
      plan,
    });
    trackUsage('usage_checkout_click', {
      plan,
      source: 'buy_button',
    }).catch(console.warn);
  };

  const handleOpenPaymentLink = (url: string, plan: 'pro_monthly' | 'pro_annual') => {
    const ok = typeof url === 'string' && url.length > 0;
    console.info('PAYWALL_CLICK', {
      mode: PAYWALL_MODE,
      monthlyOk: !!STRIPE_LINK_MONTHLY,
      annualOk: !!STRIPE_LINK_ANNUAL,
      pkTest: isTestStripePk(STRIPE_PUBLISHABLE_KEY),
    });
    if (!ok) return;
    trackUsage('usage_checkout_click', { plan, source: 'payment_link' }).catch(console.warn);

    // Open Stripe payment page
    window.open(url, '_blank', 'noopener');

    // Close paywall modal immediately
    onClose();

    // Redirect to profile page after 2 seconds (gives user time to see the Stripe page opened)
    setTimeout(() => {
      window.location.href = '/profile';
    }, 2000);
  };

  if (!isOpen) return null;

  const proFeatures = [
    'Audit-ready receipts',
    'GST/HST mapping',
    'T2125 export',
    'Legal-hold purge gate',
  ];

  const renderFeatures = (features: string[]) => (
    <ul className="mt-6 space-y-2 text-sm text-gray-700">
      {features.map(feature => (
        <li key={feature} className="flex items-start">
          <CheckIcon className="h-4 w-4 text-primary-500 mt-0.5 mr-2 flex-shrink-0" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );

  const isCurrentMonthly = currentPlan === 'PRO-MONTHLY' || currentPlan === 'PRO_TRIAL';
  const isCurrentAnnual = currentPlan === 'PRO-ANNUAL';
  const highlightAnnual = preselectedPlan === 'annual' || isCurrentMonthly;

  const handleBackToPricing = () => {
    setClientSecret(null);
    setIsLoading(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-4xl">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/80 shadow-2xl backdrop-blur-xl">
          {isTestStripePk(stripePublishableKey) && (
            <div className="absolute left-6 top-6 z-10">
              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-medium uppercase tracking-wide text-amber-700">
                Test Mode
              </span>
            </div>
          )}

          <button
            onClick={clientSecret ? handleBackToPricing : onClose}
            className="absolute right-6 top-6 text-gray-400 transition-colors hover:text-gray-600"
            aria-label={clientSecret ? 'Back to pricing' : 'Close paywall'}
          >
            {clientSecret ? (
              <ArrowLeftIcon className="h-6 w-6" />
            ) : (
              <XMarkIcon className="h-6 w-6" />
            )}
          </button>

          {/* Show Embedded Checkout if clientSecret is set */}
          {clientSecret ? (
            <div className="px-6 py-10 sm:px-10 lg:px-12">
              <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900">Complete Your Purchase</h2>
                <p className="mt-2 text-sm text-gray-600">Secure payment powered by Stripe</p>
              </div>
              <EmbeddedCheckout clientSecret={clientSecret} onClose={handleBackToPricing} />
            </div>
          ) : (
            <div className="px-6 py-10 sm:px-10 lg:px-12">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100">
                  <SparklesIcon className="h-7 w-7 text-primary-600" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 sm:text-3xl">
                  Unlock AutoTaxPilot Pro
                </h3>
                <p className="mt-2 text-sm text-slate-600 sm:text-base">
                  {isCurrentMonthly
                    ? 'Upgrade to Annual and save — everything you need for CRA compliance.'
                    : 'Everything you need for effortless CRA compliance, in a plan that fits the way you work.'}
                </p>
              </div>

              {pricingLoading ? (
                <div className="mt-10 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Loading pricing...
                  </div>
                </div>
              ) : (
                <div className="mt-10 grid gap-6 md:grid-cols-2">
                  {/* Monthly */}
                  <div
                    className={`group relative flex h-full flex-col justify-between rounded-2xl border p-6 shadow-lg transition-all hover:-translate-y-1 hover:shadow-2xl ${
                      isCurrentMonthly
                        ? 'border-primary-300 bg-gradient-to-br from-primary-50/80 via-white/90 to-white'
                        : 'border-white/40 bg-gradient-to-br from-white/90 via-white/80 to-white/60'
                    }`}
                  >
                    {isCurrentMonthly && (
                      <div className="absolute right-6 top-6">
                        <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                          Current Plan
                        </span>
                      </div>
                    )}
                    <div>
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-primary-500">
                        Pro Monthly
                      </h4>
                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="text-4xl font-semibold text-slate-900">
                          ${pricingInfo?.monthly.displayAmount || '12.00'}
                        </span>
                        <span className="text-sm text-slate-500">per month</span>
                      </div>
                      {renderFeatures(proFeatures)}
                    </div>
                    <div className="mt-8 space-y-3">
                      {useBuyButtons ? (
                        <div
                          onClick={() => handleBuyButtonClick('pro_monthly')}
                          className="rounded-xl border border-white/40 bg-white/80 p-3 shadow-inner transition-all group-hover:border-primary-200"
                        >
                          {stripePublishableKey && STRIPE_BUY_BTN_MONTHLY ? (
                            React.createElement('stripe-buy-button', {
                              'buy-button-id': STRIPE_BUY_BTN_MONTHLY,
                              'publishable-key': stripePublishableKey,
                            })
                          ) : (
                            <button
                              disabled
                              className="w-full rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-600"
                              title="Buy Button ID not configured"
                            >
                              Buy Button not configured
                            </button>
                          )}
                        </div>
                      ) : usePaymentLinks ? (
                        <button
                          onClick={() => handleOpenPaymentLink(STRIPE_LINK_MONTHLY, 'pro_monthly')}
                          disabled={isLoading !== null || !STRIPE_LINK_MONTHLY || isCurrentMonthly}
                          className="w-full rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                          title={!STRIPE_LINK_MONTHLY ? 'Payment link not configured' : undefined}
                        >
                          {isCurrentMonthly ? 'Current Plan' : 'Buy Monthly'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribe('month')}
                          disabled={isLoading !== null || isCurrentMonthly}
                          className="w-full rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isCurrentMonthly
                            ? 'Current Plan'
                            : isLoading === 'month'
                              ? 'Starting…'
                              : 'Start Monthly'}
                        </button>
                      )}
                      <p className="text-xs text-slate-500">
                        Cancel anytime. Promo codes accepted.
                      </p>
                    </div>
                  </div>

                  {/* Annual */}
                  <div
                    className={`group relative flex h-full flex-col justify-between rounded-2xl border p-6 shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl ${
                      highlightAnnual || isCurrentAnnual
                        ? 'border-primary-300 bg-gradient-to-br from-primary-50/80 via-white/90 to-white ring-2 ring-primary-200'
                        : 'border-primary-200 bg-gradient-to-br from-primary-50/80 via-white/90 to-white'
                    }`}
                  >
                    <div className="absolute right-6 top-6">
                      {isCurrentAnnual ? (
                        <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                          Current Plan
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                          {highlightAnnual ? 'Recommended' : 'Most Popular'}
                        </span>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-primary-600">
                        Pro Annual
                      </h4>
                      <div className="mt-4 flex items-baseline gap-2">
                        <span className="text-4xl font-semibold text-slate-900">
                          ${pricingInfo?.annual.displayAmount || '120.00'}
                        </span>
                        <span className="text-sm text-slate-500">per year</span>
                      </div>
                      <p className="mt-1 text-sm font-semibold text-primary-600">
                        Save ${pricingInfo?.savings.displayAmount || '24.00'} compared to monthly
                        {pricingInfo?.savings.percentage && pricingInfo.savings.percentage > 0
                          ? ` (${pricingInfo.savings.percentage}% off)`
                          : ''}
                        .
                      </p>
                      {renderFeatures(proFeatures)}
                    </div>
                    <div className="mt-8 space-y-3">
                      {useBuyButtons ? (
                        <div
                          onClick={() => handleBuyButtonClick('pro_annual')}
                          className="rounded-xl border border-primary-200 bg-white/90 p-3 shadow-inner transition-all group-hover:border-primary-300"
                        >
                          {stripePublishableKey && STRIPE_BUY_BTN_ANNUAL ? (
                            React.createElement('stripe-buy-button', {
                              'buy-button-id': STRIPE_BUY_BTN_ANNUAL,
                              'publishable-key': stripePublishableKey,
                            })
                          ) : (
                            <button
                              disabled
                              className="w-full rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-600"
                              title="Buy Button ID not configured"
                            >
                              Buy Button not configured
                            </button>
                          )}
                        </div>
                      ) : usePaymentLinks ? (
                        <button
                          onClick={() => handleOpenPaymentLink(STRIPE_LINK_ANNUAL, 'pro_annual')}
                          disabled={isLoading !== null || !STRIPE_LINK_ANNUAL || isCurrentAnnual}
                          className="w-full rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                          title={!STRIPE_LINK_ANNUAL ? 'Payment link not configured' : undefined}
                        >
                          {isCurrentAnnual ? 'Current Plan' : 'Buy Annual'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribe('year')}
                          disabled={isLoading !== null || isCurrentAnnual}
                          className="w-full rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isCurrentAnnual
                            ? 'Current Plan'
                            : isLoading === 'year'
                              ? 'Starting…'
                              : 'Start Annual'}
                        </button>
                      )}
                      <p className="text-xs text-slate-500">
                        Annual billing with 30-day cancellation window for renewals.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 text-center text-sm text-slate-500">
                Have a promo code? Enter it at checkout.
              </div>

              {(isCurrentMonthly || isCurrentAnnual) && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-center text-sm text-amber-800">
                  💡 <strong>Archive option</strong> available in Manage Billing if you want to
                  pause your subscription and keep read-only access for 6 years.
                </div>
              )}

              <div className="mt-8 text-center text-xs text-slate-500">
                By continuing, you agree to our{' '}
                <a
                  href="/terms.html"
                  className="font-semibold text-primary-600 underline"
                  onClick={onClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  href="/privacy.html"
                  className="font-semibold text-primary-600 underline"
                  onClick={onClose}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy
                </a>
                .
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={onClose}
                  className="text-sm font-medium text-slate-500 underline transition-colors hover:text-slate-700"
                >
                  Maybe later
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
